import { useState } from 'react';
import { Button, Dropdown, Menu, Confirm, State } from '@components';
import { Calendar } from '@/packs/ui/components/ui/calendar';
import { isValid } from 'date-fns';
import {
  XMarkIcon,
  EnvelopeIcon,
  ArrowDownTrayIcon,
  UserPlusIcon,
  ShareIcon,
  BellIcon,
  TrashIcon,
  FolderArrowDownIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  FlagIcon
} from '@heroicons/react/24/outline';
import { sortBy, get } from 'lodash';
import { useDeal } from './context';
import { BASE_HEIGHT_CLASS } from '../utils/constants';
import AssignModal from './assignModal';

function RenderButton({ disabled = false, count, label, icon, href, onClick }) {
  const Icon = icon;

  let wrapperClasses =
    'flex flex-col justify-center items-center relative hover:bg-blue-800 p-2 rounded-lg';

  if (disabled) {
    wrapperClasses += ' pointer-events-none opacity-60';
  }

  const buttonContent = (
    <>
      <div className="relative">
        {!disabled && count > 0 && (
          <span className="font-hmedium text-[8px] rounded-full bg-red-500 text-white absolute w-4 h-4 flex items-center justify-center right-[-10px] top-[-4px]">
            {count}
          </span>
        )}
        <Icon className="text-white w-6 h-6" />
      </div>
      <span className="text-xs font-hmedium">{label}</span>
    </>
  );

  if (onClick) {
    return (
      <button className={wrapperClasses} onClick={onClick}>
        {buttonContent}
      </button>
    );
  }

  if (!!href) {
    return (
      <a
        href={href}
        data-turbo-method="get"
        data-turbo-stream="true"
        data-turbo-frame="dialog"
        className={wrapperClasses}
      >
        {buttonContent}
      </a>
    );
  }

  return <div className={wrapperClasses}>{buttonContent}</div>;
}

function Separator() {
  return (
    <div className="h-[40px] flex">
      <div className="border-r border-blue-400 border-dashed h-[40px]"></div>
    </div>
  );
}

export function ToolbarOverlay({
  totalFilesCount,
  totalRecipientsCount,
  ids = [],
  onClose = () => {}
}) {
  const { deal, dealId, refreshing, actions = {}, setSelectedItemIds, availableStates } = useDeal();
  const hasFiles = totalFilesCount > 0;
  const hasUsers = totalRecipientsCount > 0;
  const [dateIsOpened, setDateIsOpened] = useState(false);
  const [statusIsOpened, setStatusIsOpened] = useState(false);
  const [batchDate, setBatchDate] = useState('');
  const [showEditorAssignModal, setShowEditorAssignModal] = useState(false);
  const [showViewerAssignModal, setShowViewerAssignModal] = useState(false);

  if (ids.length <= 0) {
    return null;
  }

  const urlIds = ids.map(id => `task_item_ids[]=${id}`).join('&');
  const actionArgs = { deal_id: dealId, task_item_ids: ids };
  const isLra = get(deal, 'remote_id');

  return (
    <>
      <AssignModal
        title="Qui doit compléter?"
        subtitle="Sélectionner parmi les intervenants du dossier"
        accessType="editor"
        deal={deal}
        isOpen={showEditorAssignModal}
        onClose={() => setShowEditorAssignModal(false)}
        taskItemIds={ids}
        defaultTemplateSlug="AFFECTATION_DE_DEMARCHES"
      />

      <AssignModal
        title="Qui peut consulter ?"
        subtitle="Sélectionner parmi les intervenants du dossier"
        accessType="viewer"
        deal={deal}
        isOpen={showViewerAssignModal}
        onClose={() => setShowViewerAssignModal(false)}
        taskItemIds={ids}
        defaultTemplateSlug="PARTAGE_DE_DEMARCHES"
      />

      <div className="absolute top-0 left-0 right-0 bottom-0 bg-blue-600 rounded-lg z-[2] flex justify-between items-center px-4  text-white">
        <div className="flex items-center space-x-4">
          <div className="text-[42px] font-hmedium tracking-[-0.1em]">{ids.length}</div>
          <div className="flex flex-col leading-[20px]">
            <span>élément{ids.length > 1 ? 's' : ''}</span>
            <span>sélectionné{ids.length > 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {isLra && (
            <RenderButton
              disabled={!hasFiles}
              label="Export interop"
              icon={FolderArrowDownIcon}
              count={totalFilesCount}
              href={`/deals/${dealId}/lra_export_dialog?${urlIds}`}
            />
          )}
          <RenderButton
            disabled={!hasFiles}
            label="Export email"
            icon={EnvelopeIcon}
            count={totalFilesCount}
            onClick={() => {
              actions.taskItemsExportEmail({
                ...actionArgs,
                successMessage: "L'envoi a été effectué."
              });
            }}
          />
          <RenderButton
            disabled={!hasFiles}
            label="Télécharger"
            icon={ArrowDownTrayIcon}
            count={totalFilesCount}
            onClick={() => {
              actions.taskItemsDownloadZip({
                ...actionArgs,
                successMessage: 'Téléchargement effectué.'
              });
            }}
          />
          <Separator />
          <RenderButton
            label="Affecter"
            icon={UserPlusIcon}
            onClick={() => setShowEditorAssignModal(true)}
          />
          <RenderButton
            label="Partager"
            icon={ShareIcon}
            onClick={() => setShowViewerAssignModal(true)}
          />
          <Dropdown
            isOpened={statusIsOpened}
            setIsOpened={opened => {
              setStatusIsOpened(opened);
            }}
            button={<RenderButton label="Statut" icon={FlagIcon} />}
          >
            <Menu className="w-[200px] p-2">
              <div className="flex flex-col w-full">
                {sortBy(availableStates, 'label').map(availableState => (
                  <State
                    height={BASE_HEIGHT_CLASS}
                    absolute={false}
                    key={availableState.id}
                    {...availableState}
                    onClick={() => {
                      actions.batchUpdateState({
                        ids,
                        template_status_id: availableState.id,
                        onSuccess: () => {
                          setStatusIsOpened(false);
                        },
                        successMessage:
                          ids.length > 1
                            ? 'Les démarches ont été mises à jour.'
                            : 'La démarche a été mise à jour.'
                      });
                    }}
                  />
                ))}
              </div>
            </Menu>
          </Dropdown>

          <Dropdown
            isOpened={dateIsOpened}
            setIsOpened={opened => {
              setDateIsOpened(opened);
              if (!opened) {
                setBatchDate('');
              }
            }}
            button={<RenderButton count={ids.length} label="Planifier" icon={CalendarDaysIcon} />}
          >
            <Menu className="w-[300px]">
              <Calendar
                mode="single"
                selected={batchDate}
                onSelect={date => setBatchDate(date)}
              />
              <div className="w-full py-2 flex justify-center flex-col justify-center items-center">
                <Button
                  color="blue"
                  size="sm"
                  loading={refreshing}
                  onClick={() => {
                    actions.batchUpdateEndAt({
                      ids,
                      date: batchDate,
                      onSuccess: () => {
                        setDateIsOpened(false);
                        setBatchDate('');
                      },
                      successMessage:
                        ids.length > 1
                          ? 'Les démarches ont été mises à jour.'
                          : 'La démarche a été mise à jour.'
                    });
                  }}
                  icon={ArrowRightIcon}
                  disabled={!batchDate || batchDate === '' || !isValid(batchDate)}
                  label={`Mettre à jour ${ids.length > 1 ? `les ${ids.length} échéances` : "l'échéance"}`}
                />
                <button
                  onClick={() => {
                    actions.batchUpdateEndAt({
                      ids,
                      date: '',
                      onSuccess: () => {
                        setDateIsOpened(false);
                        setBatchDate('');
                      },
                      successMessage:
                        ids.length > 1
                          ? 'Les démarches ont été mises à jour.'
                          : 'La démarche a été mise à jour.'
                    });
                  }}
                  className="text-xs mt-1.5 text-slate-400 hover:text-red-600 font-hmedium"
                >
                  Supprimer les échéances
                </button>
              </div>
            </Menu>
          </Dropdown>
          <RenderButton
            disabled={!hasUsers}
            count={totalRecipientsCount}
            label="Relancer"
            icon={BellIcon}
            href={`/deals/${dealId}/remind_dialog?${urlIds}`}
          />
          <Separator />
          <Confirm
            loading={refreshing}
            title="Supprimer les démarches ?"
            subtitle="Cette action est irréversible."
            onConfirm={() => {
              actions.batchDeleteTaskItems({
                ...actionArgs,
                successMessage:
                  ids.length > 1
                    ? 'Les démarches ont été supprimées.'
                    : 'La démarche a été supprimée.'
              });
            }}
          >
            <RenderButton label="Supprimer" icon={TrashIcon} />
          </Confirm>
        </div>
        <button onClick={() => onClose()}>
          <XMarkIcon className="text-white w-10 h-10" />
        </button>
      </div>
    </>
  );
}
